import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back, Jim!');
      navigate('/admin');
    } catch (error) {
      console.error('Auth error:', error);
      const message = error.response?.data?.detail || 'Invalid credentials';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setForgotLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: formData.email
      });
      
      toast.success(response.data.message, { duration: 8000 });
      setForgotMode(false);
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Failed to process request. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black px-4" data-testid="login-page">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="w-full max-w-md relative">
        {/* Back link */}
        <Link 
          to="/" 
          className="flex items-center text-white/60 hover:text-teal-400 transition-colors text-sm mb-8"
          data-testid="back-home-link"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        {/* Login Card */}
        <div className="bg-neutral-900 shadow-soft border border-white/10 p-8 sm:p-12">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-semibold text-white mb-2">
              Admin Login
            </h1>
            <p className="text-white/50 text-sm">
              Sign in to manage your portfolio
            </p>
          </div>

          <form onSubmit={handleSubmit} data-testid="login-form">
            <div className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                  Email Address
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="your@email.com"
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="form-input pr-10"
                    placeholder="••••••••"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
                data-testid="submit-btn"
              >
                {loading ? (
                  <div className="spinner" />
                ) : (
                  'Sign In'
                )}
              </Button>
              
              <button
                type="button"
                onClick={() => setForgotMode(true)}
                className="w-full text-center text-white/50 hover:text-teal-400 text-sm transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </button>
            </div>
          </form>
        </div>

        {/* Footer note */}
        <p className="text-center text-white/30 text-xs mt-8">
          MULTIDIO Admin - Portfolio management
        </p>
      </div>

      {/* Forgot Password Modal */}
      {forgotMode && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4" data-testid="forgot-modal">
          <div className="bg-neutral-900 border border-white/10 p-8 max-w-md w-full">
            <h2 className="font-heading text-2xl font-semibold text-white mb-2">
              Reset Password
            </h2>
            <p className="text-white/50 text-sm mb-6">
              Enter your email address and we'll send you a new password.
            </p>
            
            <form onSubmit={handleForgotPassword}>
              <div className="mb-6">
                <Label htmlFor="forgot-email" className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                  Email Address
                </Label>
                <Input
                  id="forgot-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="your@email.com"
                  data-testid="forgot-email-input"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForgotMode(false)}
                  className="btn-secondary flex-1"
                  data-testid="cancel-forgot-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="btn-primary flex-1"
                  data-testid="reset-password-btn"
                >
                  {forgotLoading ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send New Password
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
